import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { RiskAssessment } from '../../_models/risk-assessment/risk-assessment.model';
import { RiskLookup } from '../../_models/risk-assessment/risk-lookup.model';
import { BaseService } from '../base/base.service';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';

/**
 * Placement risk assessments (api/RiskAssessment) — per-setting, per-version answer sets with a
 * draft/submit/unlock lifecycle. See SDD/specs/RiskAssessmentFrontend.md (TutorPortalAPI repo).
 */
@Injectable({ providedIn: 'root' })
export class RiskAssessmentService extends BaseService {
  private apiUrl: string = environment.apiUrl;
  private lookupCache$: Observable<RiskLookup> | null = null;

  constructor(
    private http: HttpClient,
    protected hashManagerService: SessionUserHashManagerService
  ) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  /**
   * Active tuition settings + active risks with their setting mappings. Cached for the SPA session
   * after the first successful fetch — lookup data changes only by dev change request, and the cache
   * removes one of the dialog's two opening calls. A failed fetch is not cached (next call retries).
   */
  getLookup(): Observable<RiskLookup> {
    if (this.lookupCache$ == null) {
      this.lookupCache$ = this.http.get(this.apiUrl + 'api/RiskAssessment/lookup', this.prepareOptions(null)).pipe(
        map(this.unwrap),
        catchError((error) => {
          this.lookupCache$ = null;
          return this.handleError('getLookup')(error);
        }),
        shareReplay(1)
      );
    }
    return this.lookupCache$;
  }

  /** All of the placement's assessment sets (one per setting, header + answers); empty when none. */
  getByPlacement(placementId: number): Observable<RiskAssessment[]> {
    const payload = { placementId };
    return this.http.post(this.apiUrl + 'api/RiskAssessment/get', payload, this.prepareOptions(payload)).pipe(
      map(this.unwrap), catchError(this.handleError('getByPlacement'))
    );
  }

  /**
   * Starts a new version of a setting's assessment, superseding the submitted one — the old assessment
   * is kept and stays viewable. Returns the new set id. 400 when the setting has no assessment yet, its
   * current version is still a draft, or another user created the next version first.
   */
  createNew(placementId: number, riskSettingId: number): Observable<number> {
    const payload = { placementId, riskSettingId };
    return this.http.post(this.apiUrl + 'api/RiskAssessment/new', payload, this.prepareOptions(payload)).pipe(
      map(this.unwrap), catchError(this.handleError('createNew'))
    );
  }

  /** Draft save (no validation) of the (placement, setting) set. Returns the set id. 400 if locked. */
  save(assessment: RiskAssessment): Observable<number> {
    return this.http.post(this.apiUrl + 'api/RiskAssessment/save', assessment, this.prepareOptions(assessment)).pipe(
      map(this.unwrap), catchError(this.handleError('save'))
    );
  }

  /** Validated submit — locks the set. A 400's error message is the joined validation-reason string. */
  submit(assessment: RiskAssessment): Observable<number> {
    return this.http.post(this.apiUrl + 'api/RiskAssessment/submit', assessment, this.prepareOptions(assessment)).pipe(
      map(this.unwrap), catchError(this.handleError('submit'))
    );
  }

  /** Consultant unlock of a submitted set (server role-gated — 403 for everyone else). */
  unlock(id: number): Observable<boolean> {
    const payload = { id };
    return this.http.post(this.apiUrl + 'api/RiskAssessment/unlock', payload, this.prepareOptions(payload)).pipe(
      map(this.unwrap), catchError(this.handleError('unlock'))
    );
  }

  /** One assessment as a PDF (404 while the riskassessment.pdf switch is off, or when the caller may not see it). */
  downloadPdf(id: number): Observable<Blob> {
    const options = { ...this.prepareOptions(null), responseType: 'blob' as 'blob' };
    return this.http.get(this.apiUrl + 'api/RiskAssessment/' + id + '/pdf', options).pipe(
      map((data: any) => data.body as Blob), catchError(this.handleError('downloadPdf'))
    );
  }

  /** Body unwrap — 422 business-validation failures arrive on the error channel (handleError). */
  private unwrap = (data: any) => data.body;
}
